'use client';
import { ErrorIcon } from '@curology/ui-components-web/react/Icon/icons/ErrorIcon';
import { TextInput } from '@curology/ui-components-web/react/TextInput';
import { Typography } from '@curology/ui-components-web/react/Typography';
import { useMutation } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import { DATA_KEYS } from '@/consts/dataKeys';
import { TreatmentCategoryIds } from '@/consts/treatmentCategories';
import { useRouter } from '@/hooks/useRouter';
import { useServerFlashMessages } from '@/hooks/useServerFlashMessages';
import { saveAuthenticationInformation } from '@/services/authService/actions';
import {
  RegistrationService,
  type RegistrationErrorType,
  type RegistrationResponseType,
} from '@/services/registrationService/client';
import { type StoreRegistrationData } from '@/services/registrationService/types';
import { AnalyticsEvent, identifyUser, trackEvent } from '@/utils/analytics';
import { extractAnalyticsPatientData } from '@/utils/metrics/analyticsPatient';
import { urls } from '@/utils/urls';

import { errorAtom } from './atoms';
import {
  FORM_CONFIG,
  type FormSchemaType,
  type VisibleFieldType,
  getValidator,
  type VisibleErrors,
} from './config';
import {
  focusElementByName,
  focusElementToFirstError,
  getEmailSuggestion,
  mapToFormErrorMessages,
  mapToSingleError,
} from './utils';
import { useDataProvider } from '../flow/components/DataProvider/useDataProvider';
import { useFlowContext } from '../flow/hooks/useFlowContext';
import { useNavigation } from '../flow/hooks/useNavigation';
import { Button } from '../squeeze';

type RegistrationFormProps = {
  brandTransferUrl?: string | null | undefined;
  nextButtonText: string;
};

const RegistrationForm = ({
  nextButtonText,
  brandTransferUrl,
}: RegistrationFormProps) => {
  const { brand, flowType, treatmentCategory } = useFlowContext();
  const { recognizeEmailFlowFlag, eligibilityData } = useDataProvider(
    DATA_KEYS.recognizeEmailFlowFlag,
    DATA_KEYS.eligibilityData,
  );
  const recognizeEmailFlow = !!recognizeEmailFlowFlag;

  const [visibleFields, setVisibleFields] = useState(
    recognizeEmailFlow
      ? [FORM_CONFIG.email.name]
      : [FORM_CONFIG.email.name, FORM_CONFIG.password.name],
  );
  const [requestPassword, setRequestPassword] = useState(false);
  const [flashMessages, setFlashMessages] = useAtom(
    FlashMessagesState.flashMessagesAtom,
  );

  const [registrationData, setRegistrationData] = useState<
    Partial<FormSchemaType>
  >({
    state: eligibilityData.state,
    dob: eligibilityData.birthdate,
    zip: eligibilityData.zip,
    acceptedTOS: eligibilityData.hasAcceptedTerms,
    disableInstantBrandTransfer: !!brandTransferUrl,
    emailOnly: recognizeEmailFlow && !requestPassword,
  });
  const [touchedFields, setTouchedFields] = useState<
    Record<VisibleFieldType, boolean>
  >({
    email: false,
    password: false,
  });
  const { onNext } = useNavigation();
  const [error, setError] = useAtom(errorAtom);
  const serverFlashMessages = useServerFlashMessages({
    brand,
    createFlashMessageFromSearchParam: true,
  });
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const router = useRouter();
  const validator = useMemo(() => {
    return getValidator(visibleFields);
  }, [visibleFields]);
  const validation = useMemo(() => {
    return validator.safeParse(registrationData);
  }, [registrationData, validator]);
  const formErrorMessages: VisibleErrors = useMemo(() => {
    return mapToFormErrorMessages(validation, visibleFields);
  }, [validation, visibleFields]);

  const updateTouched = (key: VisibleFieldType) => {
    if (!touchedFields[key]) {
      setTouchedFields((previous) => ({ ...previous, [key]: true }));
    }
  };

  const updateRegistrationData = (
    key: keyof FormSchemaType,
    value?: string,
  ) => {
    setRegistrationData((previous) => ({
      ...previous,
      [key]: value || undefined,
    }));
  };

  const generateEmailSuggestion = (email: string) => {
    const suggestion = getEmailSuggestion(email, validator);

    if (!suggestion) {
      return;
    }

    const { full } = suggestion;
    setEmailSuggestion(full);
    trackEvent(AnalyticsEvent.EMAIL_SUGGESTION_RENDERED, {
      emailEntered: email,
      emailSuggestion: full,
    });
  };

  const registrationForm = useMutation({
    mutationFn: (data: StoreRegistrationData) =>
      RegistrationService.register(data, brand),
    onSuccess: async (response: RegistrationResponseType) => {
      if (
        response.data.isDeletingBrandAccount ||
        response.data.canTransferBrand
      ) {
        if (treatmentCategory === 'hair') {
          return;
        }

        if (!brandTransferUrl) {
          return;
        }

        router.push(brandTransferUrl);
        return;
      } else if (brand === 'curology' && response.data.recognizedEmail) {
        // Need to look into if we want recorgnizedEmail for Agency and if it works
        await saveAuthenticationInformation({
          email: response.data.recognizedEmail,
          flow: flowType,
        });
        // This page has text that's hard-coded for Curology. This would need to be fixed if we want recognizedEmail for Agency
        router.push(urls.curology.legacy.auth.niceToSeeYouAgain);
        return;
      } else if (response.data.requestPassword) {
        updateRegistrationData('emailOnly');
        setRequestPassword(true);
        setVisibleFields([FORM_CONFIG.email.name, FORM_CONFIG.password.name]);
        registrationForm.reset();
        return;
      }

      serverFlashMessages.refresh();
      // @ts-expect-error - TODO: broken by strictNullChecks
      const patient = extractAnalyticsPatientData(response.data.user);
      identifyUser({ patient });
      trackEvent(AnalyticsEvent.ACCOUNT_REGISTERED, {
        fbEventId: `account_registered_${patient.id}`,
        treatmentCategories: treatmentCategory!,
        treatmentCategoryIds: TreatmentCategoryIds[treatmentCategory!],
      });
      onSuccess();
    },
    onError: (error: RegistrationErrorType) => {
      trackEvent(AnalyticsEvent.REGISTER_FORM_ERROR, {
        backendErrors: error.response?.data.errors ?? {},
      });

      setFlashMessages([
        ...flashMessages,
        {
          children: error.response?.data.message,
          icon: <ErrorIcon />,
          iconSize: 'sm',
          state: 'critical',
          title: 'Whoops!',
        },
      ]);

      const visibleError = mapToSingleError(
        visibleFields,
        (key: VisibleFieldType) => (error.response?.data.errors ?? {})[key],
      );
      const message = Object.values(visibleError)[0];
      focusElementToFirstError(visibleError, visibleFields);

      setError(message);
      setTimeout(() => {
        setError('');
      }, 6000);
    },
  });

  const setEmailFromSuggestion = () => {
    if (emailSuggestion) {
      updateRegistrationData(FORM_CONFIG.email.name, emailSuggestion);
    }
    setEmailSuggestion(null);
    trackEvent(AnalyticsEvent.EMAIL_SUGGESTION_TAKEN);
  };

  const canSubmit = useMemo(() => {
    return !registrationForm.isPending && !registrationForm.isSuccess;
  }, [registrationForm.isPending, registrationForm.isSuccess]);

  const onBlur = (field: VisibleFieldType, value: string) => {
    updateTouched(FORM_CONFIG[field].name);
    updateRegistrationData(FORM_CONFIG[field].name, value);
    if (field === FORM_CONFIG.email.name) {
      generateEmailSuggestion(value);
    }
  };

  const onChange = (field: VisibleFieldType, value: string) => {
    updateRegistrationData(FORM_CONFIG[field].name, value);
  };

  useEffect(() => {
    requestPassword && focusElementByName(FORM_CONFIG.password.name);
  }, [requestPassword]);

  const onSuccess = () => {
    onNext();
    // Not sure why setTimeout is needed here. Without it, the navbar doesn't update
    setTimeout(router.refresh);
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit && validation.success) {
          registrationForm.mutate(registrationData);
        } else {
          focusElementToFirstError(formErrorMessages, visibleFields);
        }
      }}
    >
      <fieldset className="cur-space-y-4">
        {visibleFields.map((field, key) => {
          const state =
            touchedFields[field] && formErrorMessages[field]
              ? 'critical'
              : undefined;
          return (
            <div key={`${field}_${key}`}>
              <TextInput
                {...FORM_CONFIG[field]}
                helperText={
                  field === FORM_CONFIG.email.name &&
                  renderEmailSuggestion(setEmailFromSuggestion, emailSuggestion)
                }
                iconEnd={renderIconEnd(state)}
                onBlur={(event) => {
                  onBlur(field, event.target.value);
                }}
                onChange={(event) => {
                  onChange(field, event.target.value);
                }}
                state={state}
                value={registrationData[field]}
              />
              {renderInputError(state, formErrorMessages[field])}
            </div>
          );
        })}
      </fieldset>
      {error && (
        <Typography
          size="body"
          weight="bold"
          className="cur-pt-6 cur-text-center cur-text-error"
        >
          {error}
        </Typography>
      )}
      <Button
        disabled={!canSubmit}
        loading={registrationForm.isPending}
        className="cur-mt-8 cur-w-full"
        type="submit"
      >
        {nextButtonText}
      </Button>
    </form>
  );
};

export function renderEmailSuggestion(
  onClick: () => void,
  suggestion: string | null,
) {
  return (
    suggestion && (
      <span>
        Did you mean&nbsp;
        <button type="button" onClick={onClick} className="cur-font-bold">
          {suggestion}
        </button>
        &nbsp;?
      </span>
    )
  );
}

function renderIconEnd(errorState?: 'critical') {
  return (
    errorState && (
      // TODO: determine if this is the correct color for the error icon
      // this may need to be updated to `cur-text-error`
      <ErrorIcon className="cur-text-surface-error-bold" />
    )
  );
}

export function renderInputError(
  errorState?: 'critical',
  errorMessage?: string,
) {
  return (
    errorState && (
      <Typography size="meta" weight="bold" className="cur-mt-1 cur-text-error">
        {errorMessage}
      </Typography>
    )
  );
}

export default RegistrationForm;
